import { Injectable } from '@angular/core';
import { PostLoginContext } from './post-login.service';

const STORAGE_KEY = 'ssoPendingContext';
const MAX_AGE_MS = 10 * 60 * 1000;

export interface SsoPendingContext extends PostLoginContext {
  state: string | null;
  email: string;
  savedAt: number;
  mcpTransaction?: string | null;
}

const toPending = (raw: unknown): SsoPendingContext | null => {
  if (!raw || typeof raw !== 'object') return null;

  const candidate = raw as Partial<SsoPendingContext>;
  const returnUrl = typeof candidate.returnUrl === 'string' ? candidate.returnUrl : '';
  const mcpTransaction = typeof candidate.mcpTransaction === 'string' ? candidate.mcpTransaction : null;
  if (!returnUrl) return null;

  const queryParams = candidate.queryParams && typeof candidate.queryParams === 'object' ? candidate.queryParams : {};

  return {
    state: typeof candidate.state === 'string' ? candidate.state : null,
    email: typeof candidate.email === 'string' ? candidate.email : '',
    returnUrl,
    queryParams,
    customerParam: typeof candidate.customerParam === 'string' ? candidate.customerParam : undefined,
    mcpTransaction,
    savedAt: typeof candidate.savedAt === 'number' && isFinite(candidate.savedAt) ? candidate.savedAt : 0,
  };
};

@Injectable({ providedIn: 'root' })
export class SsoContextService {
  save(state: string | null, email: string, context: PostLoginContext, mcpTransaction: string | null = null): void {
    const entry: SsoPendingContext = { ...context, state: state || null, email, mcpTransaction, savedAt: Date.now() };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch (error) {
      console.error('SsoContextService: could not persist the sso context; the deep link will be lost', error);
    }
  }

  take(state: string | null): SsoPendingContext | null {
    const entry = this.matching(state);
    this.clear();
    return entry;
  }

  // Lee la transacción MCP pendiente de este callback sin consumir el contexto; take() lo consume después.
  pendingMcpTransaction(state: string | null): string | null {
    return this.matching(state)?.mcpTransaction ?? null;
  }

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('SsoContextService: could not clear the sso context; the deep link will be lost', error);
    }
  }

  private matching(state: string | null): SsoPendingContext | null {
    const entry = this.read();
    if (!entry || Date.now() - entry.savedAt > MAX_AGE_MS) return null;
    if (entry.state && state && entry.state !== state) return null;

    return entry;
  }

  private read(): SsoPendingContext | null {
    try {
      return toPending(JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null'));
    } catch (error) {
      console.error('SsoContextService: could not read the sso context; the deep link will be lost', error);
      return null;
    }
  }
}
